import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Breadcrumbs,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Page from '../components/Page';
import DashboardBreadcrumbLink from 'src/components/DashboardBreadcrumbLink';

const BASE = (process.env.REACT_APP_API || '').replace(/\/$/, '');
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
const ZIP_EXTS = ['.zip'];

const EMPTY_CLASS = { class_name: '', class_display_name: '', icon: '', class_icon_name: '' };
const EMPTY_VERSION = { version_display_name: '', model_name: '', model_url: '', zip_file_name: '', size_px: '640', speed_cpu: '', speed_gpu: '', map_val: 0 };
const EMPTY_MODEL_INFO = { model_id: 0, model_name: '', icon: '', model_description: '', status: 1 };

const normalizeInfo = (m = {}) => ({
  model_id: Number(m.model_id || m.id || 0),
  model_name: m.model_name || '',
  icon: m.icon || '',
  model_description: m.model_description || m.description || '',
  status: m.status ?? 1,
});
const normalizeClasses = (m = {}) =>
  (m.model_classes || m.classes || []).map((c) => ({
    class_name: c.class_name || '', class_display_name: c.class_display_name || '',
    icon: c.icon || '', class_icon_name: c.class_icon_name || '',
  }));
const normalizeVersions = (m = {}) =>
  (m.model_versions || []).map((v) => ({
    version_display_name: v.version_display_name || '', model_name: v.model_name || '',
    model_url: v.model_url || '', zip_file_name: v.zip_file_name || '',
    size_px: String(v.size_px || ''), speed_cpu: String(v.speed_cpu || ''),
    speed_gpu: String(v.speed_gpu || ''), map_val: Number(v.map_val || 0),
  }));

const buildPayload = (info, classes, versions, isNew) => {
  const payload = {
    model_name: info.model_name.trim(),
    icon: info.icon || '',
    model_description: info.model_description || '',
    model_classes: classes
      .map((c) => ({ class_name: c.class_name?.trim() || '', class_display_name: c.class_display_name?.trim() || '', icon: c.icon || '', class_icon_name: c.class_icon_name || '' }))
      .filter((c) => c.class_name || c.class_display_name || c.icon || c.class_icon_name),
    model_versions: versions
      .map((v) => ({
        version_display_name: v.version_display_name?.trim() || '', model_name: v.model_name?.trim() || '',
        model_url: v.model_url || '', zip_file_name: v.zip_file_name || '',
        size_px: !v.size_px || String(v.size_px).trim() === '' ? 0 : parseInt(v.size_px, 10),
        speed_cpu: !v.speed_cpu || String(v.speed_cpu).trim() === '' ? 0: v.speed_cpu,
        speed_gpu: !v.speed_gpu || String(v.speed_gpu).trim() === '' ? 0: v.speed_gpu,
        map_val: Number(v.map_val || 0),
      }))
      .filter((v) => v.version_display_name || v.model_name || v.model_url || v.zip_file_name),
  };
  if (!isNew) {
    payload.model_id = Number(info.model_id || 0);
    payload.status = Number(info.status ?? 1);
  }
  return payload;
};

/* ── Memoized Class Card ── */
const ClassCard = memo(function ClassCard({ cls, idx, canDelete, onUpdate, onRemove, onSetIconFile, onClearIconFile, isIconBroken, onIconError }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const softBorder = isDark ? alpha(theme.palette.common.white, 0.22) : theme.palette.grey[300];
  const softDivider = isDark ? alpha(theme.palette.common.white, 0.16) : theme.palette.grey[200];
  const fileRef = useRef(null);
  const classIconPreview = cls.class_icon_preview || cls.icon || '';

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onSetIconFile(idx, file);
  }, [idx, onSetIconFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) onSetIconFile(idx, file);
  }, [idx, onSetIconFile]);

  return (
    <Box
      id={`class-item-${idx}`}
      sx={{ border: `1px solid ${softBorder}`, borderRadius: 1.5, overflow: 'hidden', bgcolor: alpha(theme.palette.success.main, 0.04) }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${softDivider}` }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
          <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 700 }}>Class #{idx + 1}</Typography>
        </Stack>
        <IconButton size="small" color="error" onClick={() => onRemove(idx)} disabled={!canDelete} sx={{ p: 0.25 }}>
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
      <Grid container spacing={1.5} sx={{ p: 1.5 }} alignItems="stretch">
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            sx={{
              position: 'relative', p: 1.5, flex: 1, minWidth: 0, height: '100%', borderRadius: 1.25,
              border: `2px dashed ${softBorder}`, bgcolor: alpha(theme.palette.success.main, 0.04), cursor: 'pointer',
              '&:hover': { borderColor: theme.palette.success.main, bgcolor: alpha(theme.palette.success.main, 0.08) },
            }}
          >
            <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: 92 }}>
              {classIconPreview && !isIconBroken ? (
                <Box
                  component="img" src={classIconPreview} alt={cls.class_display_name || cls.class_name || 'Class icon'}
                  onError={() => onIconError(idx)}
                  sx={{ width: '100%', height: 92, borderRadius: 1, objectFit: 'cover', border: `1px solid ${softBorder}` }}
                />
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                  <Box sx={{ width: '100%', minWidth: 0, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{classIconPreview ? 'No image' : 'Drop class icon here'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{classIconPreview ? 'Image could not be loaded' : 'or click to browse (image/gif)'}</Typography>
                  </Box>
                </>
              )}
            </Stack>
            {(cls.class_icon_name || cls.icon || cls.class_icon_preview) && (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClearIconFile(idx); }} sx={{ position: 'absolute', top: 6, right: 6 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" hidden onChange={handleFileChange} />
          </Box>
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center' }}>
          <Stack spacing={1.5} sx={{ width: '100%', justifyContent: 'center' }}>
            <TextField fullWidth label="Display Name" size="small" value={cls.class_display_name} onChange={(e) => onUpdate(idx, 'class_display_name', e.target.value)} />
            <TextField fullWidth label="Class Name" size="small" value={cls.class_name} onChange={(e) => onUpdate(idx, 'class_name', e.target.value)} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
});

/* ── Memoized Version Card ── */
const VersionCard = memo(function VersionCard({ ver, idx, canDelete, onUpdate, onRemove, onSetZipFile, onClearZipFile, onClearExistingZip, onDownloadZip }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const softBorder = isDark ? alpha(theme.palette.common.white, 0.22) : theme.palette.grey[300];
  const softDivider = isDark ? alpha(theme.palette.common.white, 0.16) : theme.palette.grey[200];
  const fileRef = useRef(null);
  const canDownload = Boolean(ver.model_url) && !(ver.zip_file instanceof File);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onSetZipFile(idx, file);
  }, [idx, onSetZipFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) onSetZipFile(idx, file);
  }, [idx, onSetZipFile]);

  return (
    <Box
      id={`version-item-${idx}`}
      sx={{ border: `1px solid ${softBorder}`, borderRadius: 1.5, overflow: 'hidden', bgcolor: alpha(theme.palette.info.main, 0.04) }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${softDivider}` }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.info.main }} />
          <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 700 }}>Version #{idx + 1}</Typography>
        </Stack>
        <IconButton size="small" color="error" onClick={() => onRemove(idx)} disabled={!canDelete} sx={{ p: 0.25 }}>
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
      <Grid container spacing={1.5} sx={{ p: 1.5 }}>
        <Grid item xs={12} md={4}>
          <Box
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            sx={{
              position: 'relative', p: 1.5, minHeight: 104, borderRadius: 1.25,
              border: `2px dashed ${softBorder}`, bgcolor: alpha(theme.palette.info.main, 0.04), cursor: 'pointer',
              '&:hover': { borderColor: theme.palette.info.main, bgcolor: alpha(theme.palette.info.main, 0.08) },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CloudUploadIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Drop version zip here</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{ver.zip_file_name || 'or click to browse (.zip)'}</Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {canDownload && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownloadZip(ver.model_url); }}>
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {canDownload && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClearExistingZip(idx); }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {ver.zip_file_name && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClearZipFile(idx); }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            <input ref={fileRef} type="file" accept=".zip" hidden onChange={handleFileChange} />
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack spacing={1.5}>
            <TextField fullWidth label="Version Display Name" size="small" value={ver.version_display_name} onChange={(e) => onUpdate(idx, 'version_display_name', e.target.value)} />
            <TextField fullWidth label="Model Version Name" size="small" value={ver.model_name} onChange={(e) => onUpdate(idx, 'model_name', e.target.value)} />
          </Stack>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth label="Size (px)" size="small" value={ver.size_px} onChange={(e) => onUpdate(idx, 'size_px', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth label="mAP" size="small" type="number" inputProps={{ step: '0.01' }} value={ver.map_val} onChange={(e) => onUpdate(idx, 'map_val', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth label="Speed CPU" size="small" value={ver.speed_cpu} onChange={(e) => onUpdate(idx, 'speed_cpu', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth label="Speed GPU" size="small" value={ver.speed_gpu} onChange={(e) => onUpdate(idx, 'speed_gpu', e.target.value)} />
        </Grid>
      </Grid>
    </Box>
  );
});

export default function Model() {
  const MODEL_HEADER_PANEL_HEIGHT = 208;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sectionBg = isDark ? alpha(theme.palette.background.paper, 0.32) : theme.palette.grey[50];
  const searchInputBg = isDark ? alpha(theme.palette.background.paper, 0.42) : theme.palette.grey[50];
  const searchInputBorder = isDark ? alpha(theme.palette.common.white, 0.2) : theme.palette.grey[200];
  const listThumbBg = isDark ? alpha(theme.palette.common.white, 0.16) : theme.palette.grey[200];
  const emptyStateBg = isDark ? alpha(theme.palette.common.white, 0.12) : theme.palette.grey[100];
  const dashedBorder = isDark ? alpha(theme.palette.common.white, 0.25) : theme.palette.grey[300];
  const scrollThumb = alpha(theme.palette.text.primary, isDark ? 0.18 : 0.3);
  const scrollThumbHover = alpha(theme.palette.text.primary, isDark ? 0.28 : 0.45);
  const posterInputRef = useRef(null);
  const saveRef = useRef(null);

  // ── List state ──
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [loadingModelDetails, setLoadingModelDetails] = useState(false);
  const [deletingModelId, setDeletingModelId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  // ── Split editing state (perf: isolated re-renders) ──
  const [modelInfo, setModelInfo] = useState(null);       // { model_id, model_name, icon, model_description, status }
  const [modelClasses, setModelClasses] = useState([]);    // array of class objects
  const [modelVersions, setModelVersions] = useState([]);  // array of version objects

  // ── Search ──
  const [classSearchInput, setClassSearchInput] = useState('');
  const [classSearchText, setClassSearchText] = useState('');
  const [versionSearchText, setVersionSearchText] = useState('');
  const [pendingScroll, setPendingScroll] = useState(null);

  // ── Poster ──
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreviewUrl, setPosterPreviewUrl] = useState('');
  const [posterDragOver, setPosterDragOver] = useState(false);
  const [posterImageBroken, setPosterImageBroken] = useState(false);
  const [classIconBrokenMap, setClassIconBrokenMap] = useState({});

  const isEditing = modelInfo !== null;

  // ── Fetch models ──
  const fetchModels = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/api/v1/models`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => setModels(Array.isArray(data) ? data : []))
      .catch(() => {
        setModels([]);
        setSnack({ open: true, severity: 'error', message: 'Failed to load models' });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  useEffect(() => {
    const timer = setTimeout(() => setClassSearchText(classSearchInput), 120);
    return () => clearTimeout(timer);
  }, [classSearchInput]);

  const filteredModels = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => `${m.model_name || ''} ${m.model_description || m.description || ''}`.toLowerCase().includes(q));
  }, [models, searchText]);

  // ── Open / Close ──
  const resetEditState = useCallback(() => {
    setClassSearchInput(''); setClassSearchText(''); setVersionSearchText('');
    setPendingScroll(null); setPosterFile(null); setPosterPreviewUrl('');
    setPosterDragOver(false); setPosterImageBroken(false); setClassIconBrokenMap({});
  }, []);

  const openCreate = useCallback(() => {
    setModelInfo({ ...EMPTY_MODEL_INFO });
    setModelClasses([{ ...EMPTY_CLASS }]);
    setModelVersions([{ ...EMPTY_VERSION }]);
    resetEditState();
    setLoadingModelDetails(false);
    setIsNew(true);
    setSelectedModelId(null);
  }, [resetEditState]);

  const openEdit = useCallback((model) => {
    const id = Number(model.model_id || model.id || 0);
    setSelectedModelId(id);
    setIsNew(false);
    resetEditState();
    setLoadingModelDetails(true);
    setModelInfo(normalizeInfo(model));
    setModelClasses(normalizeClasses(model));
    setModelVersions(normalizeVersions(model));

    fetch(`${BASE}/api/v1/models?model_id=${id}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        const details = Array.isArray(data) ? data[0] : data;
        const src = details || model;
        setModelInfo(normalizeInfo(src));
        setModelClasses(normalizeClasses(src));
        setModelVersions(normalizeVersions(src));
      })
      .catch(() => {})
      .finally(() => setLoadingModelDetails(false));
  }, [resetEditState]);

  const closeEdit = useCallback(() => {
    setModelInfo(null); setModelClasses([]); setModelVersions([]);
    setSelectedModelId(null); setIsNew(false);
    setPosterFile(null); setPosterPreviewUrl(''); setPosterDragOver(false);
  }, []);

  // ── Info field setters ──
  const setInfoField = useCallback((key, value) => setModelInfo((prev) => ({ ...prev, [key]: value })), []);

  // ── Class helpers (only touch modelClasses state) ──
  const upsertClass = useCallback((idx, key, value) => {
    setModelClasses((prev) => prev.map((item, i) => (i === idx ? { ...item, [key]: value } : item)));
  }, []);

  const addClass = useCallback(() => {
    setClassSearchInput(''); setClassSearchText('');
    setModelClasses((prev) => {
      setPendingScroll({ type: 'class', index: prev.length });
      return [...prev, { ...EMPTY_CLASS }];
    });
  }, []);

  const removeClass = useCallback((idx) => {
    setModelClasses((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const setClassIconFile = useCallback((idx, file) => {
    if (!file) return;
    const ext = file.name?.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) && !ALLOWED_IMAGE_EXTS.includes(ext)) {
      setSnack({ open: true, severity: 'warning', message: 'Only JPG, PNG, WebP and GIF images are allowed' });
      return;
    }
    setModelClasses((prev) => prev.map((item, i) =>
      i === idx ? { ...item, class_icon_file: file, class_icon_name: file.name, class_icon_preview: URL.createObjectURL(file), icon: item.icon || file.name } : item
    ));
    setClassIconBrokenMap((prev) => ({ ...prev, [idx]: false }));
  }, []);

  const clearClassIconFile = useCallback((idx) => {
    setModelClasses((prev) => {
      const current = prev[idx];
      if (current?.class_icon_preview?.startsWith('blob:')) URL.revokeObjectURL(current.class_icon_preview);
      return prev.map((item, i) => i === idx ? { ...item, class_icon_file: null, class_icon_name: '', class_icon_preview: '', icon: '' } : item);
    });
  }, []);

  const onClassIconError = useCallback((idx) => {
    setClassIconBrokenMap((prev) => ({ ...prev, [idx]: true }));
  }, []);

  // ── Version helpers (only touch modelVersions state) ──
  const upsertVersion = useCallback((idx, key, value) => {
    setModelVersions((prev) => prev.map((item, i) => (i === idx ? { ...item, [key]: value } : item)));
  }, []);

  const addVersion = useCallback(() => {
    setVersionSearchText('');
    setModelVersions((prev) => {
      setPendingScroll({ type: 'version', index: prev.length });
      return [...prev, { ...EMPTY_VERSION }];
    });
  }, []);

  const removeVersion = useCallback((idx) => {
    setModelVersions((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const setVersionZipFile = useCallback((idx, file) => {
    if (!file) return;
    const ext = file.name?.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ZIP_EXTS.includes(ext)) {
      setSnack({ open: true, severity: 'warning', message: 'Only .zip files are allowed' });
      return;
    }
    setModelVersions((prev) => prev.map((item, i) =>
      i === idx ? { ...item, zip_file: file, zip_file_name: file.name, model_url: item.model_url || file.name } : item
    ));
  }, []);

  const clearVersionZipFile = useCallback((idx) => {
    setModelVersions((prev) => prev.map((item, i) => i === idx ? { ...item, zip_file: null, zip_file_name: '' } : item));
  }, []);

  const clearExistingVersionZip = useCallback((idx) => {
    setModelVersions((prev) => prev.map((item, i) => i === idx ? { ...item, zip_file: null, zip_file_name: '', model_url: '' } : item));
  }, []);

  const downloadModelZip = useCallback(async (modelUrl) => {
    if (!modelUrl) return;
    const url = /^https?:\/\//i.test(modelUrl) ? modelUrl : `${BASE}/${String(modelUrl).replace(/^\/+/, '')}`;
    try {
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        setSnack({ open: true, severity: res.status === 404 ? 'warning' : 'error', message: res.status === 404 ? 'Model zip file not found on server' : `Unable to download file (HTTP ${res.status})` });
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = url.split('/').pop()?.split('?')[0] || 'model.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setSnack({ open: true, severity: 'error', message: 'Failed to download file' });
    }
  }, []);

  // ── Poster ──
  const setPosterFromFile = useCallback((file) => {
    if (!file) return;
    const ext = file.name?.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) && !ALLOWED_IMAGE_EXTS.includes(ext)) {
      setSnack({ open: true, severity: 'warning', message: 'Only JPG, PNG, WebP and GIF images are allowed' });
      return;
    }
    setPosterFile(file);
    setPosterPreviewUrl(URL.createObjectURL(file));
    setPosterImageBroken(false);
  }, []);

  const handlePosterFileChange = useCallback((e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    setPosterFromFile(file);
  }, [setPosterFromFile]);

  const handlePosterDrop = useCallback((e) => {
    e.preventDefault(); setPosterDragOver(false);
    setPosterFromFile(e.dataTransfer?.files?.[0]);
  }, [setPosterFromFile]);

  const clearPosterSelection = useCallback(() => {
    setPosterFile(null); setPosterPreviewUrl(''); setPosterImageBroken(false);
    setModelInfo((prev) => prev ? { ...prev, icon: '' } : prev);
  }, []);

  // ── Filtered lists (depend only on their own array + search) ──
  const filteredClasses = useMemo(() => {
    const q = classSearchText.trim().toLowerCase();
    const indexed = modelClasses.map((item, idx) => ({ item, idx }));
    if (!q) return indexed;
    return indexed.filter(({ item }) => `${item.class_display_name} ${item.class_name} ${item.icon} ${item.class_icon_name}`.toLowerCase().includes(q));
  }, [modelClasses, classSearchText]);

  const filteredVersions = useMemo(() => {
    const q = versionSearchText.trim().toLowerCase();
    const indexed = modelVersions.map((item, idx) => ({ item, idx }));
    if (!q) return indexed;
    return indexed.filter(({ item }) => `${item.version_display_name} ${item.model_name} ${item.model_url} ${item.zip_file_name} ${item.size_px} ${item.speed_cpu} ${item.speed_gpu} ${item.map_val}`.toLowerCase().includes(q));
  }, [modelVersions, versionSearchText]);

  // ── Scroll to new item ──
  useEffect(() => {
    if (!pendingScroll) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`${pendingScroll.type}-item-${pendingScroll.index}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setPendingScroll(null);
    }, 0);
    return () => clearTimeout(timer);
  }, [pendingScroll, modelClasses, modelVersions]);

  useEffect(() => () => { if (posterPreviewUrl) URL.revokeObjectURL(posterPreviewUrl); }, [posterPreviewUrl]);
  useEffect(() => { setPosterImageBroken(false); }, [posterPreviewUrl, modelInfo?.icon]);

  // ── Validate ──
  const validate = useCallback(() => {
    if (!modelInfo?.model_name?.trim()) return 'Model name is required';
    if (!modelClasses.length) return 'At least one class is required';
    if (!modelVersions.length) return 'At least one version is required';
    for (const c of modelClasses) {
      if (!c.class_name?.trim()) return 'Class name is required for all classes';
      if (!c.class_display_name?.trim()) return 'Class display name is required for all classes';
    }
    for (const v of modelVersions) {
      if (!v.version_display_name?.trim()) return 'Version display name is required for all versions';
      if (!v.model_name?.trim()) return 'Model version name is required for all versions';
    }
    return '';
  }, [modelInfo, modelClasses, modelVersions]);

  // ── Save ──
  const onSave = useCallback(async () => {
    const error = validate();
    if (error) { setSnack({ open: true, severity: 'warning', message: error }); return; }

    const payload = buildPayload(modelInfo, modelClasses, modelVersions, isNew);
    const classIconFiles = modelClasses.map((cls, index) => ({ index, file: cls.class_icon_file })).filter(({ file }) => file instanceof File);
    const versionZipFiles = modelVersions.map((ver, index) => ({ index, file: ver.zip_file })).filter(({ file }) => file instanceof File);
    const url = isNew ? `${BASE}/api/v1/add-model` : `${BASE}/api/v1/update-model`;
    const method = isNew ? 'POST' : 'PUT';

    setSaving(true);
    try {
      const requestOptions = { method };
      if (versionZipFiles.length > 0 || classIconFiles.length > 0 || posterFile) {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(payload));
        if (posterFile) formData.append('model_icon', posterFile, posterFile.name);
        const classIconIndexes = [];
        classIconFiles.forEach(({ index, file }) => { formData.append('class_icons', file, file.name); classIconIndexes.push(index); });
        if (classIconIndexes.length) formData.append('class_icon_indexes', JSON.stringify(classIconIndexes));
        const modelUrlIndexes = [];
        versionZipFiles.forEach(({ index, file }) => { formData.append('model_urls', file, file.name); modelUrlIndexes.push(index); });
        if (modelUrlIndexes.length) formData.append('model_url_indexes', JSON.stringify(modelUrlIndexes));
        requestOptions.body = formData;
      } else {
        requestOptions.headers = { 'Content-Type': 'application/json' };
        
        // Rewrite payload.model_versions so size_px is 0 if empty, otherwise parseInt
        const patchedPayload = {
          ...payload,
          model_versions: Array.isArray(payload.model_versions)
            ? payload.model_versions.map((v) => ({
                ...v,
                size_px:
                  !v.size_px || String(v.size_px).trim() === ''
                    ? 0
                    : parseInt(v.size_px, 10),
                speed_cpu:
                  !v.speed_cpu || String(v.speed_cpu).trim() === ''
                    ? 0
                    :v.speed_cpu,
                speed_gpu:
                    !v.speed_gpu || String(v.speed_gpu).trim() === ''
                      ? 0
                      :v.speed_gpu 
              }))
            : payload.model_versions
        };
        requestOptions.body = JSON.stringify(patchedPayload);
      }

      const res = await fetch(url, requestOptions);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setSnack({ open: true, severity: 'success', message: isNew ? 'Model created successfully' : 'Model updated successfully' });
      const refreshedRes = await fetch(`${BASE}/api/v1/models`, { cache: 'no-store' });
      const refreshedData = await refreshedRes.json();
      const refreshedList = Array.isArray(refreshedData) ? refreshedData : [];
      setModels(refreshedList);
      setPosterFile(null); setPosterPreviewUrl(''); setPosterDragOver(false); setPosterImageBroken(false);

      if (isNew && refreshedList.length > 0) {
        const newest = refreshedList[refreshedList.length - 1];
        setSelectedModelId(Number(newest.model_id || newest.id || 0));
        setModelInfo(normalizeInfo(newest));
        setModelClasses(normalizeClasses(newest));
        setModelVersions(normalizeVersions(newest));
        setIsNew(false);
      } else if (isNew) {
        setIsNew(false);
      }
    } catch (err) {
      const message = err?.message?.includes('HTTP') ? `Server error (${err.message})` : 'Network error while saving model';
      setSnack({ open: true, severity: 'error', message });
    } finally {
      setSaving(false);
    }
  }, [validate, modelInfo, modelClasses, modelVersions, isNew, posterFile]);

  // Store latest onSave in ref for keyboard shortcut
  saveRef.current = onSave;

  // ── Ctrl+S ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (saveRef.current) saveRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Delete ──
  const confirmDeleteModel = useCallback((model, e) => { if (e) e.stopPropagation(); setDeleteConfirm(model); }, []);

  const onDeleteModel = useCallback(async () => {
    const model = deleteConfirm;
    setDeleteConfirm(null);
    if (!model) return;
    const id = Number(model?.model_id || 0);
    if (!id) return;

    setDeletingModelId(id);
    try {
      let details = model;
      try {
        const res = await fetch(`${BASE}/api/v1/models?model_id=${id}`, { cache: 'no-store' });
        const data = await res.json();
        details = Array.isArray(data) ? data[0] : data;
      } catch (_) {}

      const info = normalizeInfo(details || model);
      const classes = normalizeClasses(details || model);
      const versions = normalizeVersions(details || model);
      const payload = buildPayload(info, classes, versions, false);
      payload.status = 0;

      const res = await fetch(`${BASE}/api/v1/update-model`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      if (selectedModelId === id) closeEdit();
      setSnack({ open: true, severity: 'success', message: 'Model deleted successfully' });
      fetchModels();
    } catch (err) {
      const message = err?.message?.includes('HTTP') ? `Delete failed (${err.message})` : 'Network error while deleting model';
      setSnack({ open: true, severity: 'error', message });
    } finally {
      setDeletingModelId(null);
    }
  }, [deleteConfirm, selectedModelId, closeEdit, fetchModels]);

  const activePosterPreview = posterPreviewUrl || modelInfo?.icon || '';

  return (
    <Page title="Model Config">
      <Container
        maxWidth="xxl"
        sx={{
          mb: 3,
          height: 'calc(100vh - 120px)',
          scrollbarWidth: 'thin',
          scrollbarColor: `${scrollThumb} transparent`,
          '& *::-webkit-scrollbar': { width: 8, height: 8 },
          '& *::-webkit-scrollbar-track': { background: 'transparent' },
          '& *::-webkit-scrollbar-thumb': { backgroundColor: scrollThumb, borderRadius: 4 },
          '& *::-webkit-scrollbar-thumb:hover': { backgroundColor: scrollThumbHover },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4">Model Config</Typography>
          </Box>
          <Breadcrumbs separator="•" aria-label="breadcrumb">
            <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
            <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>Settings</Typography>
            <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Model Config</Typography>
          </Breadcrumbs>
        </Box>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* ── Left Panel: Model List ── */}
          <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  All Models {!loading && <Typography component="span" variant="caption" color="text.secondary">({models.length})</Typography>}
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontSize: 12 }}>New</Button>
              </Stack>
              <Box sx={{ px: 2, pb: 1.5 }}>
                <TextField
                  size="small" fullWidth placeholder="Search models..." value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                    sx: { borderRadius: 2, bgcolor: searchInputBg, '& fieldset': { borderColor: searchInputBorder } },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                {loading ? (
                  <Stack spacing={1} sx={{ p: 1 }}>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />)}</Stack>
                ) : filteredModels.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 6 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                      <AddIcon sx={{ color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>{searchText.trim() ? 'No matches' : 'No models yet'}</Typography>
                    {!searchText.trim() && <Button size="small" onClick={openCreate}>Create first</Button>}
                  </Stack>
                ) : (
                  filteredModels.map((model) => {
                    const id = Number(model.model_id || model.id || 0);
                    const active = selectedModelId === id;
                    return (
                      <Box
                        key={id}
                        onClick={() => openEdit(model)}
                        sx={{
                          px: 1.5, py: 1.25, mb: 0.5, borderRadius: 1.5, cursor: 'pointer',
                          bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          border: `1px solid ${active ? theme.palette.primary.main : 'transparent'}`,
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : theme.palette.action.hover },
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ width: 46, height: 46, borderRadius: 1, overflow: 'hidden', bgcolor: listThumbBg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {model.icon ? (
                                <>
                                  <Box component="img" src={model.icon} alt={model.model_name || 'poster'}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; const fb = e.currentTarget.nextElementSibling; if (fb) fb.style.display = 'flex'; }}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                  <Box sx={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                    <CloudUploadIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
                                  </Box>
                                </>
                              ) : (
                                <CloudUploadIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
                              )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" noWrap>{model.model_name || 'Untitled'}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Classes: {(model.model_classes || model.classes || []).length} | Versions: {(model.model_versions || []).length}
                              </Typography>
                            </Box>
                          </Stack>
                          <Tooltip title="Delete model" arrow>
                            <IconButton size="small" color="error" onClick={(e) => confirmDeleteModel(model, e)} disabled={deletingModelId === id}>
                              {deletingModelId === id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Card>
          </Grid>

          {/* ── Right Panel: Edit Form ── */}
          <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {!isEditing ? (
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: emptyStateBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <EditIcon sx={{ color: theme.palette.text.disabled, fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle1" color="text.secondary">Select a model to edit</Typography>
                  <Typography variant="caption" color="text.disabled">or create a new one</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 2 }}>New Model</Button>
                </Stack>
              ) : loadingModelDetails ? (
                <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">Loading model details...</Typography>
                </Stack>
              ) : (
                <>
                  {/* Header bar */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {isNew ? 'New Model' : modelInfo.model_name || 'Untitled'}
                      </Typography>
                      {!isNew && <Chip label="Editing" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button size="small" variant="outlined" onClick={closeEdit}>Cancel</Button>
                      <Tooltip title="Ctrl+S" arrow>
                        <Button size="small" variant="contained" disabled={saving} onClick={onSave} startIcon={saving ? null : <SaveIcon sx={{ fontSize: 16 }} />}>
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                    {/* Model Info Header */}
                    <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)` }}>
                      <Grid container spacing={2.5} alignItems="stretch">
                        <Grid item xs={12} md={4} sx={{ display: 'flex', height: MODEL_HEADER_PANEL_HEIGHT }}>
                          <Box
                            onClick={() => posterInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setPosterDragOver(true); }}
                            onDragLeave={() => setPosterDragOver(false)}
                            onDrop={handlePosterDrop}
                            sx={{
                              position: 'relative', minHeight: 0, borderRadius: 2, overflow: 'hidden',
                              border: `2px dashed ${posterDragOver ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.35)}`,
                              bgcolor: alpha(theme.palette.background.default, 0.6),
                              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%', width: '100%', flex: 1,
                            }}
                          >
                            {activePosterPreview && !posterImageBroken ? (
                              <Box component="img" src={activePosterPreview} alt="Poster preview" onError={() => setPosterImageBroken(true)} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                              <Stack alignItems="center" spacing={1}>
                                <CloudUploadIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                <Typography variant="subtitle2">{activePosterPreview ? 'No image' : 'Poster Preview'}</Typography>
                                <Typography variant="caption" color="text.secondary">{activePosterPreview ? 'Image could not be loaded' : 'Drop image or click to browse'}</Typography>
                              </Stack>
                            )}
                            {activePosterPreview && (
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); clearPosterSelection(); }}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  bgcolor: alpha(theme.palette.common.black, 0.55),
                                  color: 'common.white',
                                  '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.72) },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                          <input ref={posterInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" hidden onChange={handlePosterFileChange} />
                        </Grid>
                        <Grid item xs={12} md={8} sx={{ display: 'flex', height: MODEL_HEADER_PANEL_HEIGHT }}>
                          <Stack spacing={2} sx={{ height: '100%', width: '100%' }}>
                            <TextField fullWidth label="Model Name" size="small" value={modelInfo.model_name} onChange={(e) => setInfoField('model_name', e.target.value)} />
                            <TextField
                              fullWidth label="Description" size="small" multiline minRows={5} maxRows={5}
                              value={modelInfo.model_description} onChange={(e) => setInfoField('model_description', e.target.value)}
                              InputProps={{ sx: { alignItems: 'stretch', '& textarea': { overflowY: 'auto !important' } } }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Classes + Versions */}
                    <Grid container spacing={2.5}>
                      {/* ── Classes ── */}
                      <Grid item xs={12} lg={6}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2">Model Classes</Typography>
                            <Chip label={modelClasses.length} size="small" sx={{ height: 20, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 700 }} />
                          </Stack>
                          <IconButton size="small" onClick={addClass}><AddIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Stack>
                        <Box sx={{ p: 1.25, border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, bgcolor: sectionBg }}>
                          <TextField
                            size="small" fullWidth placeholder="Search classes..." value={classSearchInput}
                            onChange={(e) => setClassSearchInput(e.target.value)} sx={{ mb: 1 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                              sx: { bgcolor: searchInputBg, '& fieldset': { borderColor: searchInputBorder } },
                            }}
                          />
                          <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 0.5 }}>
                            <Stack spacing={1.5}>
                              {filteredClasses.map(({ item: cls, idx }) => (
                                <ClassCard
                                  key={`class-${idx}`}
                                  cls={cls} idx={idx}
                                  canDelete={modelClasses.length > 1}
                                  onUpdate={upsertClass}
                                  onRemove={removeClass}
                                  onSetIconFile={setClassIconFile}
                                  onClearIconFile={clearClassIconFile}
                                  isIconBroken={!!classIconBrokenMap[idx]}
                                  onIconError={onClassIconError}
                                />
                              ))}
                              {filteredClasses.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No class matches found</Typography>
                              )}
                            </Stack>
                          </Box>
                          <Box
                            onClick={addClass}
                            sx={{ mt: 1, py: 1.25, borderRadius: 1.5, cursor: 'pointer', textAlign: 'center', border: `2px dashed ${dashedBorder}`, '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                          >
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              <AddIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                              <Typography variant="body2" color="text.secondary">Add class item</Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </Grid>

                      {/* ── Versions ── */}
                      <Grid item xs={12} lg={6}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2">Model Versions</Typography>
                            <Chip label={modelVersions.length} size="small" sx={{ height: 20, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 700 }} />
                          </Stack>
                          <IconButton size="small" onClick={addVersion}><AddIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Stack>
                        <Box sx={{ p: 1.25, border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, bgcolor: sectionBg }}>
                          <TextField
                            size="small" fullWidth placeholder="Search versions..." value={versionSearchText}
                            onChange={(e) => setVersionSearchText(e.target.value)} sx={{ mb: 1 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                              sx: { bgcolor: searchInputBg, '& fieldset': { borderColor: searchInputBorder } },
                            }}
                          />
                          <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 0.5 }}>
                            <Stack spacing={1.5}>
                              {filteredVersions.map(({ item: ver, idx }) => (
                                <VersionCard
                                  key={`version-${idx}`}
                                  ver={ver} idx={idx}
                                  canDelete={modelVersions.length > 1}
                                  onUpdate={upsertVersion}
                                  onRemove={removeVersion}
                                  onSetZipFile={setVersionZipFile}
                                  onClearZipFile={clearVersionZipFile}
                                  onClearExistingZip={clearExistingVersionZip}
                                  onDownloadZip={downloadModelZip}
                                />
                              ))}
                              {filteredVersions.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No version matches found</Typography>
                              )}
                            </Stack>
                          </Box>
                          <Box
                            onClick={addVersion}
                            sx={{ mt: 1, py: 1.25, borderRadius: 1.5, cursor: 'pointer', textAlign: 'center', border: `2px dashed ${dashedBorder}`, '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                          >
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              <AddIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                              <Typography variant="body2" color="text.secondary">Add version item</Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Delete Model</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Are you sure you want to delete <strong>{deleteConfirm?.model_name || 'this model'}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button size="small" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button size="small" variant="contained" color="error" onClick={onDeleteModel}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((prev) => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((prev) => ({ ...prev, open: false }))} variant="filled" sx={{ borderRadius: 1 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Page>
  );
}
